import {
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  InputLeftElement,
  Stack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormControl, Input } from '@time-line/components/common';
import { UnitedStates } from '@time-line/components/icons';
import type { ColorScheme } from 'components/heros/PromotionHero';
import { useListSignup } from 'hooks/useListSignup';
import { useSubscribeSignup } from 'hooks/useSubscribeSignup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { usePhoneInput } from 'react-international-phone';
import { z } from 'zod';

import { SubscribeMarketingCheckbox } from './SubscribeMarketingCheckbox';
import { SubscribeTerms } from './SubscribeTerms';
import { isValidPhoneNumberForUS } from './utils/isValidPhoneNumberForUs';

const validationSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, {
      error: 'Required',
    })
    .refine((value) => isValidPhoneNumberForUS(value), {
      error: 'Invalid US phone number',
    }),
  marketingOptIn: z.boolean(),
});

type FormSchema = z.infer<typeof validationSchema>;

interface SubscribePhoneNumberProps {
  email?: string;
  emailConsent?: boolean;
  klaviyoListId?: string; // SHOP-8528: will be mandatory
  colorScheme?: ColorScheme;
  buttonColorScheme?: ColorScheme;
  autoSubscribeMarketing?: boolean | null;
}

export function SubscribePhoneNumber({
  email,
  emailConsent = true,
  klaviyoListId,
  colorScheme = 'gray',
  buttonColorScheme = 'gray',
  autoSubscribeMarketing,
}: SubscribePhoneNumberProps) {
  const subscribeAutomatically = autoSubscribeMarketing ?? true;
  const [isSubscribed, setIsSubscribed] = useState(false);
  // SHOP-8528: remove
  const { mutateAsync: mutateAsyncOld, isPending: isPendingOld } =
    useSubscribeSignup();

  const { mutateAsync, isPending } = useListSignup();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<FormSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      marketingOptIn: false,
    },
  });

  const onSubmit = async ({ phoneNumber, marketingOptIn }: FormSchema) => {
    const smsConsent = subscribeAutomatically || marketingOptIn;
    const source = window.location.pathname;

    try {
      await mutateAsync(
        {
          listId: klaviyoListId as string, // SHOP-8528: remove 'as string'
          source,
          email,
          phoneNumber,
          emailConsent,
          smsConsent,
        },
        {
          onSuccess: () => {
            setIsSubscribed(true);
          },
        },
      );
    } catch (err) {
      if (err instanceof Error) {
        setError('phoneNumber', {
          type: 'manual',
          message: err.message,
        });
      }
    }
  };

  // SHOP-8528: Remove and just use onSubmit
  const onSubmitOld = async ({ phoneNumber, marketingOptIn }: FormSchema) => {
    const smsConsent = subscribeAutomatically || marketingOptIn;
    if (!smsConsent) return;

    const path = new URL(window.location.href).pathname;

    try {
      await mutateAsyncOld(
        email && emailConsent
          ? { email, phoneNumber, attributes: { slug: path } }
          : { phoneNumber, attributes: { slug: path } },
        {
          onSuccess: () => {
            setIsSubscribed(true);
          },
        },
      );
    } catch (err) {
      if (err instanceof Error) {
        setError('phoneNumber', {
          type: 'manual',
          message: err.message,
        });
      }
    }
  };

  const { handlePhoneValueChange, inputRef, inputValue } = usePhoneInput({
    defaultCountry: 'us',
    value: '+1',
    forceDialCode: true,
    onChange: (data) => {
      setValue('phoneNumber', data.phone);
    },
  });

  const isSuccess = isSubscribed && !errors.phoneNumber;

  return (
    <Stack spacing="3xs">
      <form onSubmit={handleSubmit(klaviyoListId ? onSubmit : onSubmitOld)}>
        <FormControl isInvalid={!!errors.phoneNumber}>
          <>
            <InputGroup>
              <InputLeftElement width="xs" ml="xs" height="100%">
                <UnitedStates width="100%" />
              </InputLeftElement>
              <Input
                type="tel"
                pl="54px"
                autoComplete="tel-national"
                placeholder="Phone number"
                colorScheme={colorScheme}
                {...register('phoneNumber')}
                onChange={handlePhoneValueChange}
                ref={inputRef}
                value={inputValue}
              />
            </InputGroup>

            <FormErrorMessage
              fontSize="2xs"
              color={colorScheme === 'white' ? 'red.100' : 'red.300'}
            >
              {errors.phoneNumber?.message}
            </FormErrorMessage>

            {isSuccess && (
              <FormHelperText fontSize="2xs" color="green.600">
                Success!
              </FormHelperText>
            )}

            <Button
              type="submit"
              isLoading={isPending || isPendingOld}
              width="100%"
              mt="2xs"
              colorScheme={buttonColorScheme}
            >
              Sign up for texts
            </Button>
          </>
        </FormControl>
        {!subscribeAutomatically && (
          <SubscribeMarketingCheckbox
            colorScheme={colorScheme}
            {...register('marketingOptIn')}
          />
        )}
      </form>
      <SubscribeTerms consentType="sms" colorScheme={colorScheme} />
    </Stack>
  );
}
